import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { buildClinicalAssessmentPath } from '../../utils/clinicalAssessmentNavigation';
import { Activity, Copy, Edit3, FileHeart, Plus, Save, X } from 'lucide-react';
import { toast } from 'react-toastify';
import { supabase } from '../../config/supabase';
import {
  PATIENT_CLINICAL_EVENT_LABELS,
  PatientClinicalTimelineService,
  type PatientTimelineItem,
} from '../../services/PatientClinicalTimelineService';
import type { Client, User } from '../../services/UserService';
import {
  ANAMNESIS_SECTION_DEFINITIONS,
  CLINICAL_HISTORY_KIND,
  CLINICAL_HISTORY_TYPE_LABELS,
  STRUCTURED_ANAMNESIS_KIND,
  createEmptyAnamnesisSections,
  formatAnamnesisSections,
  getAnamnesisSections,
  getClinicalHistoryType,
  hasAnamnesisContent,
  isClinicalHistoryRecord,
  isStructuredAnamnesisRecord,
  type AnamnesisSections,
} from '../../utils/clinicalDocuments';
import { buildMedicalRecordAuthorSnapshot } from '../../utils/medicalRecordAuthor';
import { getMedicalRecordAttachmentsObject, isMedicalRecordInvalidated } from '../../utils/medicalRecordStatus';
import { getMedicalRecordSignatureMetadata } from '../../utils/documentVerification';
import ClinicalEventModal, { type EditableClinicalEvent } from './ClinicalEventModal';

interface ClinicalHistoryRecord {
  recordId: string;
  recordDate: string;
  recordDescription?: string | null;
  recordType: string;
  recordAttachments?: unknown;
}

interface InitialAnamnesisPanelProps {
  patientId: string;
  patientName?: string;
  user: User;
  selectedClient: Client;
  onCopy: (text: string) => void;
  onSaved?: () => void;
}

type EditorMode = 'create' | 'edit' | 'version';

const toLocalDateTimeInput = (value?: string) => {
  const date = value ? new Date(value) : new Date();
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
};

const plainText = (value: string) => {
  if (typeof document === 'undefined') return value;
  const container = document.createElement('div');
  container.innerHTML = value;
  return container.textContent || container.innerText || value;
};

const getTimelineLabel = (item: PatientTimelineItem) => {
  if (item.clinicalHistoryType) return CLINICAL_HISTORY_TYPE_LABELS[item.clinicalHistoryType];
  return item.kind in PATIENT_CLINICAL_EVENT_LABELS
    ? PATIENT_CLINICAL_EVENT_LABELS[item.kind as keyof typeof PATIENT_CLINICAL_EVENT_LABELS]
    : 'Evento clínico';
};

const getDocumentVersion = (record?: ClinicalHistoryRecord | null): number => {
  if (!record) return 0;
  const attachments = getMedicalRecordAttachmentsObject(record.recordAttachments);
  const version = Number(attachments.clinicalDocumentVersion);
  return Number.isFinite(version) && version > 0 ? version : 1;
};

const buildSectionsFromLegacyRecords = (records: ClinicalHistoryRecord[]): AnamnesisSections => {
  const sections = createEmptyAnamnesisSections();

  for (const record of records) {
    if (isStructuredAnamnesisRecord(record.recordAttachments)) continue;
    const value = plainText(record.recordDescription || '').trim();
    if (!value) continue;

    switch (getClinicalHistoryType(record.recordAttachments)) {
      case 'initial_anamnesis':
      case 'hma':
        sections.currentIllnessHistory ||= value;
        break;
      case 'personal_history':
        sections.personalHistory ||= value;
        break;
      case 'surgical_history':
        sections.surgicalHistory ||= value;
        break;
      case 'family_history':
        sections.familyHistory ||= value;
        break;
      case 'medications_allergies':
        sections.medications ||= value;
        break;
      case 'anamnesis':
        sections.additionalNotes ||= value;
        break;
      default:
        break;
    }
  }

  return sections;
};

const InitialAnamnesisPanel: React.FC<InitialAnamnesisPanelProps> = ({
  patientId,
  patientName,
  user,
  selectedClient,
  onCopy,
  onSaved,
}) => {
  const location = useLocation();
  const [latestAnamnesisRecord, setLatestAnamnesisRecord] = useState<ClinicalHistoryRecord | null>(null);
  const [historyRecords, setHistoryRecords] = useState<ClinicalHistoryRecord[]>([]);
  const [contextItems, setContextItems] = useState<PatientTimelineItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showContextModal, setShowContextModal] = useState(false);
  const [editorMode, setEditorMode] = useState<EditorMode | null>(null);
  const [sections, setSections] = useState<AnamnesisSections>(createEmptyAnamnesisSections);
  const [recordDate, setRecordDate] = useState(toLocalDateTimeInput());
  const [saving, setSaving] = useState(false);
  const [eventToEdit, setEventToEdit] = useState<EditableClinicalEvent | null>(null);

  const loadContext = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('medical_records')
        .select('recordId, recordDate, recordDescription, recordType, recordAttachments')
        .eq('clientId', selectedClient.clientId)
        .eq('patientId', patientId)
        .order('recordDate', { ascending: false });

      if (error) throw error;
      const records = (data || []) as ClinicalHistoryRecord[];
      const validHistoryRecords = records.filter(
        (item) =>
          isClinicalHistoryRecord(item.recordAttachments) &&
          !isMedicalRecordInvalidated(item.recordType, item.recordAttachments),
      );
      setHistoryRecords(validHistoryRecords);
      const structuredRecords = validHistoryRecords.filter((record) =>
        isStructuredAnamnesisRecord(record.recordAttachments),
      );
      const latestStructuredRecord = structuredRecords.reduce<ClinicalHistoryRecord | null>(
        (latest, record) => {
          if (!latest || getDocumentVersion(record) > getDocumentVersion(latest)) return record;
          if (
            getDocumentVersion(record) === getDocumentVersion(latest) &&
            new Date(record.recordDate).getTime() > new Date(latest.recordDate).getTime()
          ) {
            return record;
          }
          return latest;
        },
        null,
      );
      setLatestAnamnesisRecord(latestStructuredRecord);

      try {
        const timeline = await PatientClinicalTimelineService.getTimeline(
          patientId,
          selectedClient.clientId,
        );
        setContextItems(
          timeline.filter(
            (item) =>
              item.kind === 'initial_anamnesis' ||
              item.kind === 'clinical_history' ||
              item.source === 'clinical_event',
          ),
        );
      } catch (timelineError) {
        console.warn('Não foi possível carregar eventos no contexto clínico:', timelineError);
        setContextItems(
          validHistoryRecords.map((record) => {
            const attachments = getMedicalRecordAttachmentsObject(record.recordAttachments);
            return {
              id: record.recordId,
              source: 'medical_record',
              kind: attachments.clinicalDocumentKind === CLINICAL_HISTORY_KIND
                ? 'clinical_history'
                : attachments.clinicalDocumentKind === STRUCTURED_ANAMNESIS_KIND
                  ? 'clinical_history'
                  : 'initial_anamnesis',
              title: CLINICAL_HISTORY_TYPE_LABELS[getClinicalHistoryType(record.recordAttachments)],
              description: record.recordDescription || 'Sem conteúdo registrado.',
              date: record.recordDate,
              documentVersion: Number(attachments.clinicalDocumentVersion) || 1,
              clinicalHistoryType: getClinicalHistoryType(record.recordAttachments),
            } as PatientTimelineItem;
          }),
        );
      }
    } catch (error) {
      console.error('Erro ao buscar contexto clínico:', error);
      toast.error('Não foi possível carregar o contexto clínico do paciente.');
      setLatestAnamnesisRecord(null);
      setHistoryRecords([]);
      setContextItems([]);
    } finally {
      setLoading(false);
    }
  }, [patientId, selectedClient.clientId]);

  useEffect(() => {
    void loadContext();
  }, [loadContext]);

  const isSigned = Boolean(
    latestAnamnesisRecord && getMedicalRecordSignatureMetadata(latestAnamnesisRecord.recordAttachments),
  );

  const currentVersion = useMemo(() => {
    return getDocumentVersion(latestAnamnesisRecord);
  }, [latestAnamnesisRecord]);

  const latestAnamnesisSections = useMemo(
    () => getAnamnesisSections(latestAnamnesisRecord?.recordAttachments),
    [latestAnamnesisRecord],
  );

  const openEditor = (mode: EditorMode) => {
    setEditorMode(mode);
    if (mode === 'create') {
      setSections(buildSectionsFromLegacyRecords(historyRecords));
      setRecordDate(toLocalDateTimeInput());
      return;
    }

    setSections(getAnamnesisSections(latestAnamnesisRecord?.recordAttachments));
    setRecordDate(
      toLocalDateTimeInput(mode === 'edit' ? latestAnamnesisRecord?.recordDate : undefined),
    );
  };

  const openCurrentAction = () => {
    if (!latestAnamnesisRecord) {
      openEditor('create');
    } else if (isSigned) {
      openEditor('version');
    } else {
      openEditor('edit');
    }
  };

  const saveAnamnesis = async () => {
    if (!recordDate || !hasAnamnesisContent(sections)) {
      toast.error('Informe a data e preencha pelo menos uma seção da anamnese.');
      return;
    }

    setSaving(true);
    try {
      const description = formatAnamnesisSections(sections);
      const currentAttachments = getMedicalRecordAttachmentsObject(
        latestAnamnesisRecord?.recordAttachments,
      );
      const authorSnapshot = buildMedicalRecordAuthorSnapshot({
        userId: user.userId,
        name: user.userDisplayName || null,
        role: selectedClient.userRole || user.userRole || null,
      });

      if (editorMode === 'edit' && latestAnamnesisRecord) {
        const { error } = await supabase
          .from('medical_records')
          .update({
            recordDate: new Date(recordDate).toISOString(),
            recordTitle: currentVersion > 1 ? 'Atualização da anamnese' : 'Anamnese',
            recordDescription: description,
            recordAttachments: {
              ...currentAttachments,
              clinicalDocumentKind: STRUCTURED_ANAMNESIS_KIND,
              clinicalHistoryType: currentVersion > 1 ? 'anamnesis_update' : 'anamnesis',
              schemaVersion: 1,
              anamnesisSections: sections,
              authorSnapshot: currentAttachments.authorSnapshot || authorSnapshot,
              lastEditedBy: authorSnapshot,
              lastEditedAt: new Date().toISOString(),
            },
            recordUpdatedAt: new Date().toISOString(),
          })
          .eq('recordId', latestAnamnesisRecord.recordId)
          .eq('clientId', selectedClient.clientId);
        if (error) throw error;
        toast.success('Anamnese atualizada.');
      } else {
        const nextVersion = editorMode === 'version' ? currentVersion + 1 : 1;
        const { error } = await supabase.from('medical_records').insert({
          clientId: selectedClient.clientId,
          patientId,
          doctorId: user.userId,
          recordType: 'progress',
          recordDate: new Date(recordDate).toISOString(),
          recordTitle: nextVersion > 1 ? 'Atualização da anamnese' : 'Anamnese',
          recordDescription: description,
          recordAttachments: {
            clinicalDocumentKind: STRUCTURED_ANAMNESIS_KIND,
            clinicalHistoryType: nextVersion > 1 ? 'anamnesis_update' : 'anamnesis',
            schemaVersion: 1,
            anamnesisSections: sections,
            clinicalDocumentVersion: nextVersion,
            previousClinicalDocumentId:
              editorMode === 'version' ? latestAnamnesisRecord?.recordId || null : null,
            authorSnapshot,
          },
        });
        if (error) throw error;
        toast.success(editorMode === 'version' ? 'Nova versão da anamnese criada.' : 'Anamnese registrada.');
      }

      setEditorMode(null);
      await loadContext();
      onSaved?.();
    } catch (error) {
      console.error('Erro ao salvar anamnese:', error);
      toast.error('Não foi possível salvar a anamnese.');
    } finally {
      setSaving(false);
    }
  };

  const actionLabel = !latestAnamnesisRecord ? 'Preencher' : isSigned ? 'Atualizar' : 'Editar';

  const openEventEditor = (item: PatientTimelineItem) => {
    if (item.source !== 'clinical_event') return;

    setEventToEdit({
      eventId: item.id,
      eventType: item.kind as EditableClinicalEvent['eventType'],
      severity: item.severity || 'moderate',
      eventTitle: item.title,
      eventDescription: item.description,
      eventDate: item.date,
    });
  };

  return (
    <>
      <div className="flex min-h-16 w-full items-stretch rounded-xl border border-slate-200 bg-white transition-colors hover:border-teal-300">
        <button
          type="button"
          onClick={() => setShowContextModal(true)}
          className="flex min-w-0 flex-1 items-center rounded-l-xl px-3 py-2.5 text-left hover:bg-teal-50/40"
        >
          <span className="flex min-w-0 flex-1 items-center gap-3">
            <span className="shrink-0 rounded-lg bg-teal-50 p-2 text-teal-700">
              <FileHeart className="h-4 w-4" />
            </span>
            <span className="min-w-0">
              <span className="block truncate text-sm font-semibold text-slate-900">Anamnese</span>
              <span className="block truncate text-xs text-slate-500">
                {loading
                  ? 'Carregando...'
                  : latestAnamnesisRecord
                    ? `Atualizada em ${new Date(latestAnamnesisRecord.recordDate).toLocaleDateString('pt-BR')}`
                    : 'Não preenchida'}
              </span>
            </span>
          </span>
        </button>
        <button
          type="button"
          onClick={openCurrentAction}
          disabled={loading}
          className="inline-flex shrink-0 items-center gap-1 border-l border-slate-200 px-3 text-xs font-semibold text-teal-700 hover:bg-teal-50 disabled:opacity-50"
        >
          {latestAnamnesisRecord && !isSigned ? (
            <Edit3 className="h-4 w-4" />
          ) : (
            <Plus className="h-4 w-4" />
          )}
          {actionLabel}
        </button>
      </div>

      {showContextModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4">
          <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-start justify-between border-b border-slate-200 p-5">
              <div>
                <h2 className="text-xl font-semibold text-slate-900">Anamnese e histórico clínico</h2>
                <p className="mt-1 text-sm text-slate-500">{patientName || 'Paciente selecionado'}</p>
              </div>
              <button type="button" onClick={() => setShowContextModal(false)} className="text-slate-400 hover:text-slate-700">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="overflow-y-auto p-5">
              <div className="mb-5 rounded-xl border border-teal-100 bg-teal-50/60 p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">
                      {latestAnamnesisRecord ? 'Anamnese registrada' : 'Paciente sem anamnese estruturada'}
                    </p>
                    <p className="mt-1 text-xs text-slate-600">
                      {latestAnamnesisRecord
                        ? `${new Date(latestAnamnesisRecord.recordDate).toLocaleString('pt-BR')} · versão ${currentVersion}${isSigned ? ' · assinada' : ''}`
                        : historyRecords.length > 0
                          ? 'Os registros antigos serão aproveitados no primeiro preenchimento.'
                          : 'Preencha as informações clínicas iniciais do paciente.'}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button type="button" onClick={openCurrentAction} className="inline-flex items-center gap-1 rounded-md bg-teal-700 px-3 py-2 text-xs font-semibold text-white hover:bg-teal-800">
                      {latestAnamnesisRecord && !isSigned ? <Edit3 className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                      {actionLabel}
                    </button>
                    {latestAnamnesisRecord && (
                      <Link to={buildClinicalAssessmentPath(location.pathname, patientId, latestAnamnesisRecord.recordId)} className="inline-flex items-center gap-1 rounded-md border border-teal-200 bg-white px-3 py-2 text-xs font-semibold text-teal-800 hover:bg-teal-50">
                        <FileHeart className="h-4 w-4" /> Continuar avaliação
                      </Link>
                    )}
                    {latestAnamnesisRecord && (
                      <button type="button" onClick={() => onCopy(latestAnamnesisRecord.recordDescription || '')} className="inline-flex items-center gap-1 rounded-md border border-teal-200 bg-white px-3 py-2 text-xs font-semibold text-teal-800 hover:bg-teal-50">
                        <Copy className="h-3.5 w-3.5" /> Copiar
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {latestAnamnesisRecord && (
                <div className="mb-6 grid gap-3 sm:grid-cols-2">
                  {ANAMNESIS_SECTION_DEFINITIONS.map(({ key, label }) => {
                    const value = latestAnamnesisSections[key].trim();
                    if (!value) return null;
                    return (
                      <section key={key} className="rounded-xl border border-slate-200 p-3 sm:last:col-span-2">
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</h3>
                        <p className="mt-1 whitespace-pre-wrap text-sm leading-6 text-slate-700">{value}</p>
                      </section>
                    );
                  })}
                </div>
              )}

              <div>
                <h3 className="mb-3 text-sm font-semibold text-slate-900">Histórico do paciente</h3>
                {loading ? (
                  <p className="text-sm text-slate-500">Carregando histórico clínico...</p>
                ) : contextItems.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">
                    Nenhum registro clínico encontrado.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {contextItems.map((item) => (
                      <article key={`${item.source}-${item.id}`} className="rounded-xl border border-slate-200 p-4">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div className="flex items-center gap-2">
                            {item.source === 'clinical_event' ? (
                              <Activity className="h-4 w-4 text-amber-600" />
                            ) : (
                              <FileHeart className="h-4 w-4 text-teal-700" />
                            )}
                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                              {getTimelineLabel(item)}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <time className="text-xs text-slate-500">
                              {new Date(item.date).toLocaleString('pt-BR')}
                            </time>
                            {item.source === 'clinical_event' && (
                              <button
                                type="button"
                                onClick={() => openEventEditor(item)}
                                className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-xs font-semibold text-slate-700 hover:border-teal-300 hover:bg-teal-50 hover:text-teal-800"
                                aria-label={`Editar ${item.title}`}
                              >
                                <Edit3 className="h-3.5 w-3.5" />
                                Editar
                              </button>
                            )}
                          </div>
                        </div>
                        <p className="mt-2 text-sm font-semibold text-slate-900">{item.title}</p>
                        <p className="mt-1 whitespace-pre-wrap text-sm leading-6 text-slate-700">
                          {plainText(item.description)}
                        </p>
                      </article>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <ClinicalEventModal
        isOpen={Boolean(eventToEdit)}
        patientId={patientId}
        clientId={selectedClient.clientId}
        patientName={patientName}
        userId={user.userId}
        userDisplayName={user.userDisplayName}
        eventToEdit={eventToEdit}
        onClose={() => setEventToEdit(null)}
        onSaved={async () => {
          await loadContext();
          onSaved?.();
        }}
      />

      {editorMode && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-3 sm:p-4">
          <div className="flex max-h-[94vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-start justify-between border-b border-slate-200 p-4 sm:p-5">
              <div>
                <h2 className="text-xl font-semibold text-slate-900">
                  {editorMode === 'create'
                    ? 'Preencher anamnese'
                    : editorMode === 'version'
                      ? 'Atualizar anamnese'
                      : 'Editar anamnese'}
                </h2>
                <p className="mt-1 text-sm text-slate-500">{patientName || 'Paciente selecionado'}</p>
              </div>
              <button type="button" onClick={() => setEditorMode(null)} className="text-slate-400 hover:text-slate-700">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="overflow-y-auto p-4 sm:p-5">
              {editorMode === 'version' && (
                <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                  A versão anterior está assinada e será preservada. Ao salvar, uma nova versão será criada.
                </div>
              )}
              {editorMode === 'create' && historyRecords.length > 0 && hasAnamnesisContent(sections) && (
                <div className="mb-4 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-900">
                  Alguns campos foram preenchidos com registros antigos. Revise o conteúdo antes de salvar.
                </div>
              )}

              <div className="mb-5 max-w-sm">
                <label className="mb-1 block text-sm font-medium text-slate-700">Data e hora da anamnese</label>
                <input
                  type="datetime-local"
                  value={recordDate}
                  onChange={(event) => setRecordDate(event.target.value)}
                  className="w-full rounded-lg border-slate-300"
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                {ANAMNESIS_SECTION_DEFINITIONS.map(({ key, label, placeholder }) => (
                  <div key={key} className={key === 'currentIllnessHistory' || key === 'additionalNotes' ? 'sm:col-span-2' : ''}>
                    <label className="mb-1 block text-sm font-medium text-slate-700">{label}</label>
                    <textarea
                      value={sections[key]}
                      onChange={(event) =>
                        setSections((current) => ({ ...current, [key]: event.target.value }))
                      }
                      rows={key === 'currentIllnessHistory' ? 5 : 3}
                      placeholder={placeholder}
                      className="w-full resize-y rounded-lg border-slate-300 text-sm leading-6"
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-col-reverse gap-2 border-t border-slate-200 p-4 sm:flex-row sm:justify-end sm:p-5">
              <button type="button" onClick={() => setEditorMode(null)} disabled={saving} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
                Cancelar
              </button>
              <button type="button" onClick={() => void saveAnamnesis()} disabled={saving} className="inline-flex items-center justify-center gap-2 rounded-lg bg-teal-700 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-800 disabled:opacity-60">
                <Save className="h-4 w-4" /> {saving ? 'Salvando...' : 'Salvar anamnese'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default InitialAnamnesisPanel;
